/**
 * Local state, the offline write queue, and sync.
 *
 * The load-bearing property: local state is the last server snapshot FOLDED
 * WITH the pending outbox, never the snapshot alone. The engine then runs
 * against that merged state, so the leaderboard is live and correct in airplane
 * mode — your quota total updates as you enter holes with no network at all.
 * That falls out for free from storing only gross scores and deriving
 * everything else.
 *
 * Bandon has poor coverage across most of the property, so nothing in the
 * scoring path may ever block on the network.
 */

import { useSyncExternalStore } from 'react'
import type { PressMode, SideMatch } from './engine/sideMatch'
import type { Forecast } from './engine/weather'
import type {
  Group,
  HoleScore,
  HoleScoreValue,
  Match,
  Pairing,
  Round,
  Tournament,
} from './engine/types'

const KEY = 'woodsmen:v1'

export interface CardConfirmation {
  roundId: string
  groupId: string
  confirmedBy: string
  confirmedAt: string
}

/**
 * A birdie that was announced, and is still true.
 *
 * "Still true" is doing real work in that sentence. These arrive and leave on
 * the sync cursor like scores do, so a hole re-scored back to a par withdraws
 * its own row and it disappears from the feed on every phone. The notification
 * that went out cannot be taken back; this can, and that is the whole reason
 * the feed is not simply a list of what was pushed.
 */
/**
 * A putt missed from inside three feet.
 *
 * Not a score: it changes nobody's card and nobody's money. It is reported
 * by whoever is holding the pencil, and it counts for the rest of the week.
 */
export interface Miss {
  roundId: string
  playerId: string
  holeNo: number
  reportedBy: string
  /** Null while it is still inside the thirty seconds and takeable back. */
  announcedAt: number | null
}

export interface Alert {
  roundId: string
  playerId: string
  holeNo: number
  gross: number
  par: number
  /** "Birdie", "Eagle", "Albatross", "Hole in one". */
  kind: string
  /** Where tapping it goes; null when the round has no match. */
  matchId: string | null
  announcedAt: number
  /** Phones the push actually reached. Zero is ordinary, not an error. */
  sent: number
}

export interface QueuedWrite {
  /** Minted once at enqueue; survives retries, restarts and reinstalls. */
  idempotencyKey: string
  roundId: string
  playerId: string
  holeNo: number
  /** `cleared` is a deletion travelling as a write — see the server's SCORE_STATES. */
  state: HoleScoreValue['state'] | 'cleared'
  gross: number | null
  deviceId: string
  createdAt: number
}

/**
 * A side match write waiting for signal.
 *
 * A union rather than one shape with optional fields, because the two writes are
 * genuinely different mutations and the key is what tells the server they are:
 * a create carries the whole deal, a void carries only which match and when.
 */
export type QueuedSideMatch =
  | {
      idempotencyKey: string
      kind: 'create'
      match: SideMatch
      /** Carried on the entry, not passed to the flush: the man who insisted on
       *  a second bet against the same opponent did so about THIS match, and the
       *  flush that eventually delivers it may be a background tick. */
      allowDuplicate?: boolean
    }
  | {
      idempotencyKey: string
      kind: 'void'
      matchId: string
      /*
       * Minted at enqueue, not read from the clock when the fold runs.
       *
       * The fold below sits inside a memoised `useSyncExternalStore` snapshot,
       * and a timestamp that moved on every call would defeat the cache from
       * the inside — which is React error #185 and a white screen. Same trap,
       * same fix, as the queued card confirmations above.
       */
      at: string
      by: string
    }

export interface AppState {
  token: string | null
  /**
   * The week's forecast, as last fetched.
   *
   * Persisted with everything else, so a phone with no signal on the first tee
   * still shows what it knew this morning — stamped with when, because a
   * forecast that cannot say how old it is invites being trusted more than it
   * deserves. Null until the first fetch; nobody is owed weather.
   */
  weather: Forecast | null
  session: { tournamentId: string; playerId: string; isAdmin: boolean } | null
  deviceId: string
  cursor: number
  /** Structure only — scores live separately so they can merge granularly. */
  doc: Tournament | null
  /**
   * The version `doc` was fetched at — the fork point for an edit, not a cursor.
   *
   * Separate from `cursor` because the cursor is dragged forward by every score
   * anybody enters, and a setup draft does not go stale because somebody made a
   * par. Sending the cursor as the base of a doc save meant the optimistic check
   * failed when nothing had changed and passed when the document had been
   * overwritten underneath it.
   */
  docVersion: number
  serverScores: HoleScore[]
  outbox: QueuedWrite[]
  online: boolean
  /*
   * Whether the server actually answers — which is not the same question.
   *
   * `navigator.onLine` reports the network interface, not the internet. A phone
   * latched onto a lodge wifi it has not signed into reports online and cannot
   * reach anything, and that is the normal failure on the property rather than
   * an exotic one. So reachability is measured by whether requests come back,
   * and the app says "offline" when either signal says so.
   */
  reachable: boolean
  /*
   * The admin token, held ALONGSIDE the player token rather than replacing it.
   *
   * It lasts half an hour and the trip lasts five days. Swapping it in meant
   * that once it expired the admin's own phone had one dead credential and
   * nothing else — so every call 401'd, including scoring, and the device was
   * useless until it was signed out and back in. Keeping both means expiry
   * costs exactly what it should: admin rights, and nothing else.
   */
  adminToken: string | null
  /** When the server will stop honoring the admin token. */
  adminUntil: number | null
  /** Last admin action on this device — what the idle lock counts from. */
  adminTouchedAt: number | null
  /** Cards the server says are signed for. Replaced wholesale on every pull. */
  confirmations: CardConfirmation[]
  /** Birdies and better, newest last. Merged on the cursor, like scores. */
  alerts: Alert[]
  /** Putts missed from inside three feet — the week's shame ledger. */
  misses: Miss[]
  /*
   * Sign-offs this phone has made and not yet delivered.
   *
   * Kept apart from the score outbox because they are a different kind of
   * write — one row per group, idempotent, and safe to replay in any order —
   * but they queue for the same reason: the 18th green is where signal is
   * worst and where this button gets pressed.
   */
  confirmOutbox: { roundId: string; groupId: string; confirm: boolean }[]
  /**
   * Live side matches, as the server holds them. Replaced wholesale on every
   * pull, exactly like `confirmations` — see the note in the API's `getState`.
   */
  sideMatches: SideMatch[]
  /*
   * Side matches struck or called off on this phone and not yet delivered.
   *
   * Queued for the reason everything on the Score tab is queued — the first tee
   * and the 18th green are where the signal is worst and where these two buttons
   * get pressed — and carrying an idempotency key for the reason `placeBet`
   * carries one: a request whose response was lost must cost nothing to replay,
   * and a duplicated Nassau is not a cosmetic problem.
   */
  sideOutbox: QueuedSideMatch[]
  syncing: boolean
  lastSyncAt: number | null
  error: string | null
  /** Last scores-cleared marker seen from the server. See Tournament.scoresEpoch. */
  scoresEpoch: number
}

const initial = (): AppState => ({
  token: null,
  weather: null,
  session: null,
  deviceId: crypto.randomUUID(),
  cursor: 0,
  doc: null,
  docVersion: 0,
  serverScores: [],
  outbox: [],
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  // Assumed until proven otherwise: starting pessimistic would show an offline
  // card for the first second of every launch.
  reachable: true,
  adminToken: null,
  adminUntil: null,
  adminTouchedAt: null,
  syncing: false,
  lastSyncAt: null,
  error: null,
  scoresEpoch: 0,
  confirmations: [],
  confirmOutbox: [],
  alerts: [],
  misses: [],
  sideMatches: [],
  sideOutbox: [],
})

let state: AppState = load()
const listeners = new Set<() => void>()

/**
 * Rebuild state from what was saved, re-measuring anything that is a fact
 * about right now rather than a preference.
 *
 * `online` and `reachable` are both persisted by `persist()` along with
 * everything else, so a plain spread of the saved blob restores whatever they
 * were when the app was last closed. `reachable` was already being reset;
 * `online` was not — and it is the worse of the two, because it is read from
 * `navigator.onLine` at construction and thereafter moved only by the
 * online/offline events. A phone that was offline when the app was last closed
 * and online when it was next opened fires no event, so a stale `false` stuck:
 * the status light showed amber and the offline card sat there while every
 * request succeeded.
 */
export function rehydrate(saved: Partial<AppState>, isOnline: boolean): AppState {
  return { ...initial(), ...saved, syncing: false, online: isOnline, reachable: true }
}

function load(): AppState {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return initial()
    return rehydrate(
      JSON.parse(raw) as Partial<AppState>,
      typeof navigator === 'undefined' ? true : navigator.onLine,
    )
  } catch {
    return initial()
  }
}

function persist(): void {
  try {
    const { syncing: _s, error: _e, ...rest } = state
    localStorage.setItem(KEY, JSON.stringify(rest))
  } catch {
    // A full quota must not take scoring down; the outbox still lives in memory.
  }
}

function set(patch: Partial<AppState>): void {
  state = { ...state, ...patch }
  persist()
  for (const l of listeners) l()
}

export const getState = (): AppState => state

export function useStore<T>(select: (s: AppState) => T): T {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => select(state),
    () => select(state),
  )
}

// ---------------------------------------------------------------------------
// the fold — snapshot + outbox = what the UI and engine actually see
// ---------------------------------------------------------------------------

/**
 * Memoised on the identity of the state object.
 *
 * `mergedScores` is handed straight to `useStore`, so React uses its result as
 * a `useSyncExternalStore` snapshot and compares it with Object.is on every
 * render. Returning a freshly built array each call reads as "changed" every
 * time and spins the render loop until React aborts with error #185. `set()`
 * always replaces the state object, so caching against that identity
 * invalidates exactly when the data really does change.
 */
let mergedCache: { key: AppState; value: HoleScore[] } | null = null

export function mergedScores(s: AppState = state): HoleScore[] {
  if (mergedCache && mergedCache.key === s) return mergedCache.value
  const value = foldScores(s)
  mergedCache = { key: s, value }
  return value
}

function foldScores(s: AppState): HoleScore[] {
  const byCell = new Map<string, HoleScore>()
  const key = (r: string, p: string, h: number) => `${r}|${p}|${h}`

  for (const sc of s.serverScores) {
    byCell.set(key(sc.roundId, sc.playerId, sc.holeNo), sc)
  }
  // Pending writes always win locally — the tap you just made must be what you
  // see, even if the server has not heard about it yet.
  for (const w of s.outbox) {
    // A queued clear takes the cell away locally, exactly as it will when it
    // lands: the man who fixed it must not go on seeing the number he removed.
    if (w.state === 'cleared') {
      byCell.delete(key(w.roundId, w.playerId, w.holeNo))
      continue
    }
    byCell.set(key(w.roundId, w.playerId, w.holeNo), {
      roundId: w.roundId,
      playerId: w.playerId,
      holeNo: w.holeNo,
      value:
        w.state === 'holed'
          ? { state: 'holed', gross: w.gross ?? 0 }
          : w.state === 'pickedUp'
            ? { state: 'pickedUp', mostLikely: w.gross }
            : { state: 'conceded' },
      enteredBy: s.session?.playerId ?? '',
      updatedAt: w.createdAt,
      deviceId: w.deviceId,
    })
  }
  return [...byCell.values()]
}

/** Is this cell still waiting to reach the server? */
export function isPending(roundId: string, playerId: string, holeNo: number): boolean {
  return state.outbox.some(
    (w) => w.roundId === roundId && w.playerId === playerId && w.holeNo === holeNo,
  )
}

// ---------------------------------------------------------------------------
// writes
// ---------------------------------------------------------------------------

/**
 * Record a score. Never awaits the network.
 *
 * Repeated taps on the same cell COALESCE into the pending write rather than
 * queuing three of them — tapping 5, 6, 7 sends one write, not three.
 */
export function setScore(
  roundId: string,
  playerId: string,
  holeNo: number,
  value: HoleScoreValue,
): void {
  const existing = state.outbox.find(
    (w) => w.roundId === roundId && w.playerId === playerId && w.holeNo === holeNo,
  )
  const write: QueuedWrite = {
    /*
     * A fresh key for every edit, even when it replaces a queued one.
     *
     * Reusing the key looks like the point of coalescing and quietly destroys
     * corrections. The key may already be on the wire, or already recorded
     * server-side from a request whose response was lost; either way the server
     * treats a known key as "already done" WITHOUT looking at the payload, and
     * answers `applied`, at which point the client drops the write. Tap 5, fix
     * it to 6, and the 6 is gone — no error, no pending dot, nothing in the
     * audit trail, and the cell silently reverts to 5.
     *
     * Coalescing still does its job below: one queued write per cell, so three
     * taps send one request. What must not be shared is the identity of the
     * mutation, because the two values genuinely are different mutations.
     * Ordering is already handled — scores are last-write-wins per cell.
     */
    idempotencyKey: crypto.randomUUID(),
    roundId,
    playerId,
    holeNo,
    state: value.state,
    gross:
      value.state === 'holed'
        ? value.gross
        : value.state === 'pickedUp'
          ? value.mostLikely
          : null,
    deviceId: state.deviceId,
    createdAt: Date.now(),
  }
  set({
    outbox: existing
      ? state.outbox.map((w) => (w === existing ? write : w))
      : [...state.outbox, write],
  })
  void flush()
}

/**
 * Take a hole back off the card.
 *
 * The missing half of scoring: until now a wrong number could only be
 * overwritten with another number, so a score typed on the wrong man's row —
 * or on a hole nobody had played — stayed in his total until an admin wiped
 * the whole card. This says "nobody played this", and it travels as an
 * ordinary write: same queue, same offline behaviour, same refusal once the
 * card is signed, and it withdraws a birdie that has already gone out.
 */
export function clearScore(roundId: string, playerId: string, holeNo: number): void {
  const existing = state.outbox.find(
    (w) => w.roundId === roundId && w.playerId === playerId && w.holeNo === holeNo,
  )
  const write: QueuedWrite = {
    idempotencyKey: crypto.randomUUID(),
    roundId,
    playerId,
    holeNo,
    state: 'cleared',
    gross: null,
    deviceId: state.deviceId,
    createdAt: Date.now(),
  }
  set({
    outbox: existing
      ? state.outbox.map((w) => (w === existing ? write : w))
      : [...state.outbox, write],
  })
  void flush()
}

// ---------------------------------------------------------------------------
// network
// ---------------------------------------------------------------------------

/*
 * How long to wait before deciding the network is not there.
 *
 * A request with no deadline is the one failure the four-state status light
 * cannot see. `reachable` only goes false when a fetch REJECTS, and a captive
 * portal or a saturated resort access point does not reject — it accepts the
 * connection and swallows the packets. So the light stayed green, the pill said
 * "Sending 5 scores…", and the offline card that exists to say "nothing is lost
 * by closing the app" never appeared, on precisely the network it was written
 * for. Measured: twenty seconds against a black hole, still green.
 *
 * Eight seconds because the service worker already treats three as the limit of
 * patience for the shell, and a score POST deserves more rope than a page load
 * — but not unbounded rope.
 */
const NET_TIMEOUT_MS = 8000

export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const send = (bearer: string | null) =>
    fetch(`/api/${path}`, {
      ...init,
      // A hung request must fail like an absent one, or nothing downstream can
      // tell the difference — and an AbortError rejects, which is what makes
      // `reachable` and the offline card work. A caller's own signal wins,
      // because one endpoint genuinely is slow rather than hung: the photo
      // reader spends its time waiting on a model, and eight seconds of rope
      // measured for a score POST would hang every card photo ever taken.
      signal: init?.signal ?? AbortSignal.timeout(NET_TIMEOUT_MS),
      headers: {
        'content-type': 'application/json',
        ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
        ...init?.headers,
      },
    })

  let usedAdmin = state.adminToken !== null
  let res = await send(state.adminToken ?? state.token)

  /*
   * An expired admin token demotes; it does not lock anyone out.
   *
   * The admin credential is the short-lived one, so it is the one that dies
   * mid-week. When it does, drop it and immediately retry as the player — the
   * scoring, the leaderboard and the sync all keep working, and the only thing
   * lost is the ability to save setup, which is exactly what expired.
   */
  if ((res.status === 401 || res.status === 403) && state.adminToken) {
    set({
      adminToken: null,
      session: state.session ? { ...state.session, isAdmin: false } : state.session,
    })
    usedAdmin = false
    res = await send(state.token)
  }

  /*
   * A captive portal answers 200 with HTML, and that used to read as success.
   *
   * Resort and hotel wifi intercepts every request until you accept its terms,
   * and what comes back is a login page with a 200 on it. `json()` rejects, the
   * catch turns it into `{}`, `res.ok` is true — so `login` set an undefined
   * token and returned happily, the app stayed on the sign-in screen, and the
   * button went from "Signing in…" straight back to "Sign in" with no error at
   * all. Eleven men doing that at once on the first tee is the worst version of
   * this app's worst afternoon.
   *
   * So a reply that is not JSON is a failure however healthy its status, and it
   * says the one thing that actually helps.
   */
  /*
   * Any admin action pushes the idle lock back, not just editing Setup.
   *
   * `touchAdmin` was called from exactly one place — inside `setDraft` — so
   * unlocking a card, clearing a card, confirming a Venmo, settling a market,
   * posting a line and voiding a bet all left the ten-minute clock running. An
   * admin who elevated and then spent twelve minutes fixing four cards across
   * three foursomes on the Score tab was demoted mid-task, and because
   * `demote()` clears `session.isAdmin` the controls vanished from under his
   * thumb. Hanging it off the one funnel every request goes through catches
   * every admin action, including ones nobody has written yet.
   */
  if (usedAdmin && res.ok) touchAdmin()

  const raw = await res.text()
  let body: unknown = {}
  try {
    body = raw ? JSON.parse(raw) : {}
  } catch {
    throw Object.assign(
      new Error(
        'This network wants you to sign in first — open a browser tab, accept the wifi terms, then try again.',
      ),
      { status: 0, body: {} },
    )
  }
  if (!res.ok)
    throw Object.assign(new Error((body as any).error ?? res.statusText), {
      status: res.status,
      body,
    })
  return body as T
}

export async function login(tournamentId: string, playerId: string): Promise<void> {
  const r = await call<{ token: string; session: AppState['session'] }>('login', {
    method: 'POST',
    body: JSON.stringify({ tournamentId, playerId }),
  })
  // Belt and braces on the same failure: JSON that simply has no token in it
  // must not be stored as a session, or the screen sits there saying nothing.
  if (!r.token) throw new Error('Signed in, but the server sent no session back. Try again.')
  /*
   * Crossing to another tournament resets everything the old one owned.
   *
   * The sign-in screen is normally reached through `logout()`, which already
   * clears all of this — but any path that reaches `login` still carrying the
   * previous week's cursor, document or epoch would poison the new one: a
   * cursor above the new tournament's version makes the server withhold its
   * document forever. Same-tournament re-login (wrong name picked) keeps its
   * state, because it is the same week.
   */
  if (r.session && state.session && r.session.tournamentId !== state.session.tournamentId) {
    set({
      doc: null,
      docVersion: 0,
      cursor: 0,
      serverScores: [],
      outbox: [],
      sideMatches: [],
      sideOutbox: [],
      confirmOutbox: [],
      confirmations: [],
      alerts: [],
  misses: [],
      scoresEpoch: 0,
    })
  }
  set({ token: r.token, session: r.session, error: null })
  await pull()
}

/**
 * Earn an admin token by knowing the setup PIN, and keep the player one.
 *
 * They are stored side by side because they expire on different clocks — a day
 * against thirty. `call` prefers the admin token and falls back to the player
 * token the moment the server refuses it, so the end of admin rights is a
 * demotion rather than a dead phone.
 */
/**
 * Unlock Setup. The PIN is optional — a man on the roster's admin list needs none.
 */
export async function elevate(pin?: string): Promise<void> {
  const r = await call<{ token: string; session: AppState['session']; expiresAt?: number }>(
    'elevate',
    { method: 'POST', body: JSON.stringify(pin ? { pin } : {}) },
  )
  set({
    adminToken: r.token,
    session: r.session,
    adminUntil: r.expiresAt ?? Date.now() + 1000 * 60 * 30,
    adminTouchedAt: Date.now(),
    error: null,
  })
}

/**
 * Push the idle lock back, because the admin is plainly still there.
 *
 * Called on every edit. Without it, a long job — building a draw, retyping
 * twelve handicaps — locks itself halfway through and throws away the draft,
 * which is a worse outcome than the one the lock exists to prevent.
 */
export function touchAdmin(): void {
  if (state.adminToken) set({ adminTouchedAt: Date.now() })
}

/** Give up admin rights on this device, keeping the player signed in. */
export function demote(): void {
  set({
    adminToken: null,
    adminUntil: null,
    session: state.session ? { ...state.session, isAdmin: false } : state.session,
  })
}

export function logout(): void {
  set({
    token: null,
    adminToken: null,
    session: null,
    doc: null,
    docVersion: 0,
    serverScores: [],
    cursor: 0,
    outbox: [],
    // Somebody else's Nassau is nobody's business on a phone that has been
    // handed over, and a queued create belongs to the man who struck it — it
    // must not be delivered under the next player's token.
    sideMatches: [],
    sideOutbox: [],
    /*
     * The sign-off queue above all: a queued confirmation delivered under the
     * NEXT player's token would put HIS name on a card he never saw — round
     * and group ids are identical across sign-ins on the same tournament, so
     * nothing downstream would ever notice. The confirmations and alerts are
     * the previous man's view of the week and go for the same hygiene.
     */
    confirmOutbox: [],
    confirmations: [],
    alerts: [],
  misses: [],
    error: null,
  })
  // A standing notice was an instruction to the previous player.
  sticky = false
}

/*
 * The in-flight guards are TIMESTAMPS, not booleans.
 *
 * A boolean assumes every request eventually settles. On a phone it does
 * not: background the app mid-POST, or let it sleep on a resort access
 * point, and the fetch neither resolves nor rejects — so the `finally` that
 * clears the flag never runs, and from then on every flush returns at the
 * front door. The queue sits there forever behind a green dot, and the
 * "Send now" button quietly does nothing, because it calls the same wedged
 * function. Found on Scott's phone with two scores stranded for hours while
 * every poll succeeded.
 *
 * A stamp lets the next attempt take over once the last one has plainly
 * died. Nothing is sent twice by it: every write carries an idempotency key
 * the server already de-duplicates.
 */
const IN_FLIGHT_MS = 30_000
const inFlight = (since: number): boolean => since > 0 && Date.now() - since < IN_FLIGHT_MS
let flushingSince = 0

/**
 * Is the current error one the player has to act on?
 *
 * Transient failures clear themselves on the next good sync, which is right —
 * nobody needs a toast about a lost second of signal. A dropped score is the
 * opposite: it is a standing instruction to write a number down again, and the
 * very next successful flush used to wipe it off the screen a moment after it
 * appeared. Sticky notices survive success and are cleared only by the player.
 */
let sticky = false

/** Dismiss a standing notice. The player has read it. */
export function dismissError(): void {
  sticky = false
  set({ error: null })
}

/**
 * Say something to the player that a successful sync must not wipe out.
 *
 * The same channel as a dropped score, for the same reason: it is an explanation
 * of something the app declined to do, so it has to outlive the next good poll.
 * Only the player clears it.
 */
export function notice(message: string): void {
  sticky = true
  set({ error: message })
}

/**
 * Why this queued write can never be accepted, or null if it can.
 *
 * The outbox is persisted, so it outlives the build that wrote it: an entry
 * left by an older client, or one that came back from a half-written
 * localStorage, can be structurally impossible for the server to store. Those
 * are dropped here rather than sent, because a request that fails the same way
 * every time is not a retry, it is a queue that has stopped — and everything
 * behind it in the outbox is somebody's real round.
 *
 * Deliberately the same line the server draws in `readMutation`, and no
 * stricter. This is a last check for entries that predate the current code, not
 * a place to have opinions about golf; anything it refuses is gone for good.
 */
function unsendable(w: QueuedWrite): string | null {
  if (!w || typeof w !== 'object') return 'not a score'
  if (typeof w.idempotencyKey !== 'string' || w.idempotencyKey.length === 0) return 'no key'
  if (typeof w.roundId !== 'string' || w.roundId.length === 0) return 'no round'
  if (typeof w.playerId !== 'string' || w.playerId.length === 0) return 'no player'
  if (typeof w.holeNo !== 'number' || !Number.isInteger(w.holeNo)) return 'no hole'
  if (
    w.state !== 'holed' &&
    w.state !== 'pickedUp' &&
    w.state !== 'conceded' &&
    // A deletion travelling as a write — see `clearScore`. It was missing
    // here, so every clear was dropped by the poison filter as malformed and
    // announced to the man as a score that "could not be saved".
    w.state !== 'cleared'
  ) {
    return `unknown state "${String(w.state)}"`
  }
  if (w.state === 'holed' && typeof w.gross !== 'number') return 'no stroke count'
  return null
}

/**
 * Drop everything in the outbox that will never be accepted, and say so.
 *
 * Said out loud, unlike a transient failure. A score that is never going to
 * reach the server is exactly the case the silent-retry policy is not for: the
 * pending dot would otherwise sit there for five days meaning nothing, and the
 * player whose 6 is being discarded is the only person who can write it down
 * again.
 */
function dropPoison(): string | null {
  const poison = state.outbox.filter((w) => unsendable(w) !== null)
  if (poison.length === 0) return null
  // Filtered by identity, not by key — a missing key is one of the reasons an
  // entry lands here in the first place.
  const dead = new Set(poison)
  const note = `${poison.length} score${poison.length > 1 ? 's' : ''} could not be saved (${unsendable(poison[0]!)}) and had to be dropped. Re-enter ${poison.length > 1 ? 'them' : 'it'}.`
  sticky = true
  set({ outbox: state.outbox.filter((w) => !dead.has(w)), error: note })
  // Returned as well as set, because the rest of this flush will succeed and
  // clear the error on its way out. A message that appears for one frame and
  // vanishes is not a message.
  return note
}

/**
 * What to tell the man whose scores the server would not take.
 *
 * Two kinds of refusal, and they call for opposite advice, which is why this is
 * no longer one sentence with a count in it.
 *
 * A malformed write, a deleted round, a card already signed for — nothing was
 * stored and nothing is going to be, so the number is lost and somebody has to
 * type it again. That is the standing instruction the sticky notice was built
 * for.
 *
 * A stale write is the opposite and telling him to re-enter it would be actively
 * harmful. It was refused BECAUSE the hole already holds a newer number: his
 * phone was out of signal, the group fixed the hole while it was, and his queue
 * caught up afterwards. The card in front of him is right. He still has to be
 * told, though — he watched himself enter a 5, the app said nothing, and if he
 * finds a 6 there later the reasonable conclusion is that the app lost his
 * score and the card needs correcting. Which would put the 5 back.
 */
function refusalNotice(dead: { reason: string; stale?: boolean }[]): string {
  const stale = dead.filter((d) => d.stale === true).length
  const lost = dead.length - stale
  const parts: string[] = []
  if (stale > 0) {
    parts.push(
      `${stale} score${stale > 1 ? 's' : ''} entered on this phone did not take — the hole had already been re-scored while you had no signal. The card on screen is the current one; do not enter ${stale > 1 ? 'them' : 'it'} again.`,
    )
  }
  if (lost > 0) {
    // Named from the first of its own kind, not from `dead[0]`, which may be a
    // stale one and would then explain the wrong refusal.
    const why = dead.find((d) => d.stale !== true)!.reason
    parts.push(
      `${lost} score${lost > 1 ? 's were' : ' was'} refused (${why}). Re-enter ${lost > 1 ? 'them' : 'it'}.`,
    )
  }
  return parts.join(' ')
}

/** Push the outbox, then pull anything new. Safe to call at any time. */
export async function flush(): Promise<void> {
  if (inFlight(flushingSince) || !state.token || !navigator.onLine || state.outbox.length === 0) return

  // Before the head of the queue is even looked at, and over the whole outbox
  // rather than just the batch — a poison entry at position 60 would otherwise
  // wait, invisibly, for the fifty in front of it to clear and then wedge
  // everything again.
  const dropped = dropPoison()
  if (state.outbox.length === 0) return

  flushingSince = Date.now()
  set({ syncing: true })
  try {
    const batch = state.outbox.slice(0, 50)
    const r = await call<{
      applied: string[]
      /** Writes the server has judged impossible. Absent from older servers. */
      rejected?: { idempotencyKey: string | null; reason: string; stale?: boolean }[]
      version: number
    }>('scores', {
      method: 'POST',
      body: JSON.stringify({
        /*
         * This clock, read now, so the server can tell how old the queue is.
         *
         * `createdAt` on its own is useless to anybody else: twelve phones do not
         * agree on the time, and a device an hour fast would win every argument
         * about who typed what first. Sent alongside a reading of the SAME clock
         * taken as the request leaves, the difference is the write's age — and an
         * age is skew-free, because the offset cancels between the two readings.
         * The server subtracts it from its own clock, so everything it stores is
         * on one clock and comparable. See `decidedAtFor` in the API.
         *
         * Read here rather than at enqueue on purpose: a batch that fails is
         * retried, and each attempt has to report the age as it is at that
         * attempt, not as it was an hour ago.
         */
        sentAt: Date.now(),
        mutations: batch.map((w) => ({
          idempotencyKey: w.idempotencyKey,
          roundId: w.roundId,
          playerId: w.playerId,
          holeNo: w.holeNo,
          state: w.state,
          gross: w.gross,
          deviceId: w.deviceId,
          /*
           * The field the whole ordering bug came down to.
           *
           * It has been on the queue entry since the outbox was written and was
           * simply never put in the body, so the server had nothing to order by
           * except arrival — and a phone flushing an hour of queued scores
           * overwrote corrections made since with no sign of it anywhere.
           *
           * `undefined` on an entry queued by an older build, which drops the key
           * from the JSON and is handled: the server reads a missing age as "no
           * evidence this is stale" and stores it.
           */
          createdAt: w.createdAt,
        })),
      }),
    })
    /*
     * Pull BEFORE dropping the sent writes, never after.
     *
     * The outbox is what makes a tap appear instantly: `mergedScores` folds it
     * over the last server snapshot. Clearing it first opens a window — however
     * short — where the score is in neither place, and the cell visibly falls
     * back to its old value and then snaps forward when the pull lands. That is
     * the flicker you see on every tap. Pulling first means the number is
     * covered by the outbox or by the snapshot at every instant, with no gap in
     * between.
     *
     * Filtering by key rather than replacing the array matters too: a player
     * can tap again while this request is in flight, and that newer write must
     * survive.
     */
    /*
     * Rejected writes leave the outbox alongside the applied ones.
     *
     * "The server has looked at this and it can never be stored" is a different
     * answer from "the request did not arrive", and only the second one is
     * worth retrying. Keeping a rejected write would put it back at the head of
     * the queue on the next tick, in front of every score entered since, which
     * is the poison-pill loop this endpoint used to produce as a 500.
     */
    const dead = r.rejected ?? []
    const done = new Set([...r.applied, ...dead.map((x) => x.idempotencyKey)])
    await pull()
    if (dead.length > 0) sticky = true
    set({
      outbox: state.outbox.filter((w) => !done.has(w.idempotencyKey)),
      error:
        dead.length > 0
          ? refusalNotice(dead)
          : // A routine success must not retract a standing notice.
            (dropped ?? (sticky ? state.error : null)),
      /*
       * The batch came back, so whatever the network was doing, it is doing it
       * again now — and `online` is set from the same evidence.
       *
       * `navigator.onLine` is a declaration; a reply is proof. On iOS the
       * declaration can be wrong for minutes at a time and fires no event when
       * it corrects itself, so a phone that had gone quiet stayed amber while
       * every request it made succeeded. Trusting the reply over the flag is
       * what makes the light recover on its own.
       */
      online: true,
      reachable: true,
    })
  } catch (e) {
    // Transient failures stay silent — they resolve themselves, and a toast
    // mid-round is worse than a quiet pending dot.
    const status = (e as { status?: number }).status
    if (status === undefined) {
      // No status at all means the request never reached a server: DNS, a
      // dropped connection, or a captive portal eating it. That is the case
      // `navigator.onLine` cannot see.
      set({ reachable: false })
    } else if (status >= 400 && status < 500 && status !== 409) {
      set({ error: e instanceof Error ? e.message : 'Sync failed' })
    }
  } finally {
    flushingSince = 0
    set({ syncing: false })
  }
}

/*
 * One poll at a time.
 *
 * `startSync` fires every eight seconds regardless of whether the last one came
 * back. On a network that hangs rather than refuses, that stacked a new request
 * every tick — and at roughly six sockets per host the queue starved the score
 * POST behind it, so the one write that mattered was blocked by a pile of polls
 * nobody was waiting for. The timeout in `call` bounds each one now; this stops
 * them overlapping in the first place.
 */
let pollingSince = 0

export async function pull(): Promise<void> {
  if (!state.token || !navigator.onLine) return
  if (inFlight(pollingSince)) return
  pollingSince = Date.now()
  try {
    const r = await call<{
      version: number
      docVersion?: number
      doc: Tournament | null
      scores: {
        round_id: string
        player_id: string
        hole_no: number
        state: string
        gross: number | null
        entered_by: string
        entered_at: string
        /** When it was typed, on the server's clock. Absent from older servers. */
        decided_at?: number | null
        deleted?: number
      }[]
      tiebreaks: any[]
      confirmations?: {
        round_id: string
        group_id: string
        confirmed_by: string
        confirmed_at: string
      }[]
      alerts?: {
        round_id: string
        player_id: string
        hole_no: number
        gross: number
        par: number
        kind: string
        match_id: string | null
        announced_at: string
        sent: number
        deleted?: number
      }[]
      misses?: {
        round_id: string
        player_id: string
        hole_no: number
        reported_by: string
        reported_at: string
        announced_at: string | null
        sent: number
        deleted?: number
      }[]
      /** Whole on every poll, not on the cursor. See the API's `getState`. */
      sideMatches?: {
        id: string
        round_ids: string
        player_a: string
        player_b: string
        strokes: number
        amount_cents: number
        press_mode: string
        created_by: string
        created_at: string
        voided_at: string | null
        voided_by: string | null
      }[]
      /*
       * A phone holding no document asks for everything, whatever its cursor
       * says. The server withholds the doc whenever `since` has reached the
       * doc's version — "you already have it" — which is the right answer to
       * every client except one that somehow has the cursor without the doc.
       * That one was unrecoverable: every tab renders "Loading the
       * tournament…", including the one with the sign-out button, and every
       * poll re-asks the question that got it stuck. Scott's phone found the
       * state on the dry run; belt-and-braces below in `login` closes the
       * likeliest way in.
       */
    }>(`state?since=${state.doc ? state.cursor : 0}`)

    /*
     * Safe to adopt whether or not the document came down with it.
     *
     * The server withholds the document precisely when this client's copy is
     * already at that version, so "no doc" and "doc unchanged" are the same
     * statement. The fallback is for a server that predates the field, where
     * standing still is the only honest thing to do.
     */
    const docVersion = r.docVersion ?? state.docVersion

    /*
     * A cleared cell arrives as a row, not as an absence.
     *
     * The cursor can only carry rows, so a deletion is a row with `deleted` set
     * and a fresh version — which is the whole reason the column exists. This
     * used to map every row in regardless, so clearing a card server-side would
     * have left every phone still showing it.
     *
     * Kept separate from the epoch mechanism above on purpose: an epoch bump
     * means "everything you hold is gone" and throws away the outbox with it,
     * which is right for a full reset and much too violent for one card.
     */
    const removed = r.scores.filter((row) => row.deleted)
    const incoming: HoleScore[] = r.scores.filter((row) => !row.deleted).map((row) => ({
      roundId: row.round_id,
      playerId: row.player_id,
      holeNo: row.hole_no,
      value:
        row.state === 'holed'
          // A holed row always carries a gross; the column is nullable only
          // because a picked-up hole may have no most-likely.
          ? { state: 'holed', gross: row.gross as number }
          : row.state === 'pickedUp'
            ? { state: 'pickedUp', mostLikely: row.gross }
            : { state: 'conceded' },
      enteredBy: row.entered_by,
      /*
       * When the score was DECIDED, which is what the outbox side of this fold
       * has always meant by `updatedAt` — it uses the queued write's `createdAt`.
       *
       * `entered_at` is the server's receive time, so a write that spent an hour
       * in somebody's queue was dated to the moment it landed. That made the two
       * halves of `mergedScores` disagree about the same field: a pending score
       * was timed from the tap and the identical score, once acknowledged,
       * jumped forward to whenever the network happened to come back.
       *
       * Falls back to the receive time for a server that predates the column,
       * and for the rows written before it existed, which is the old meaning and
       * the only thing those rows can honestly say.
       */
      updatedAt: row.decided_at ?? Date.parse(row.entered_at),
      deviceId: '',
    }))

    // An admin cleared the scores. The cursor cannot carry a deletion, so the
    // only safe reading of a new epoch is that everything held locally is gone
    // — including anything still queued, which belongs to a week that no longer
    // exists and would otherwise re-post itself.
    const epoch = r.doc?.scoresEpoch ?? state.scoresEpoch
    if (epoch !== state.scoresEpoch) {
      // Rewind to zero and refetch rather than trusting this payload. It was
      // fetched against the OLD cursor, so it holds whatever survived the reset
      // mixed with whatever has been entered since, and there is no way to tell
      // the two apart. Adopting its version while discarding its rows would
      // strand every score below that version permanently — the cursor only
      // ever moves forward.
      set({
        cursor: 0,
        doc: r.doc ?? state.doc,
        docVersion,
        serverScores: [],
        outbox: [],
        // The birdies belonged to the week that was just deleted. The server
        // dropped them outright rather than soft-deleting, precisely because
        // this line exists to throw them away here.
        alerts: [],
  misses: [],
        /*
         * The side matches go too, along with anything queued about one.
         *
         * They were bets on eighteen holes that no longer exist. Kept, every one
         * of them would recompute to all square against an empty card with money
         * still against a man's name — and a queued create would re-post itself
         * into the replacement week. The server deleted its rows outright for
         * exactly this line's benefit.
         */
        sideMatches: [],
        sideOutbox: [],
        scoresEpoch: epoch,
        error: null,
      })
      // A notice about the old week has nothing to stand on after a reset.
      sticky = false
      /*
       * Released before re-entering, or the guard above swallows the refetch.
       *
       * This branch rewinds the cursor to zero and calls `pull` again to fetch
       * the replacement week. With `polling` still held that recursive call
       * would return immediately, leaving the client at cursor 0 with no scores
       * until the next tick — which would look exactly like a reset that ate
       * the week.
       */
      pollingSince = 0
      await pull()
      return
    }

    const merged = new Map(
      state.serverScores.map((s) => [`${s.roundId}|${s.playerId}|${s.holeNo}`, s]),
    )
    for (const s of incoming) merged.set(`${s.roundId}|${s.playerId}|${s.holeNo}`, s)
    for (const row of removed) merged.delete(`${row.round_id}|${row.player_id}|${row.hole_no}`)

    /*
     * Same merge, same reason: a withdrawn birdie arrives as a row with
     * `deleted` set, and dropping it from the map is what makes it leave the
     * feed. Mapping every row in regardless would leave a corrected score
     * showing as a birdie on every phone that had already seen it.
     */
    const mergedAlerts = new Map(
      state.alerts.map((a) => [`${a.roundId}|${a.playerId}|${a.holeNo}`, a]),
    )
    for (const row of r.alerts ?? []) {
      const key = `${row.round_id}|${row.player_id}|${row.hole_no}`
      if (row.deleted) {
        mergedAlerts.delete(key)
        continue
      }
      mergedAlerts.set(key, {
        roundId: row.round_id,
        playerId: row.player_id,
        holeNo: row.hole_no,
        gross: row.gross,
        par: row.par,
        kind: row.kind,
        matchId: row.match_id,
        announcedAt: Date.parse(row.announced_at),
        sent: row.sent ?? 0,
      })
    }

    /*
     * The shame ledger merges exactly like the alerts: a miss taken back
     * arrives as a deleted row, and dropping it from the map is what makes
     * it leave every phone.
     */
    const mergedMisses = new Map(
      state.misses.map((m) => [`${m.roundId}|${m.playerId}|${m.holeNo}`, m]),
    )
    for (const row of r.misses ?? []) {
      const key = `${row.round_id}|${row.player_id}|${row.hole_no}`
      if (row.deleted) {
        mergedMisses.delete(key)
        continue
      }
      mergedMisses.set(key, {
        roundId: row.round_id,
        playerId: row.player_id,
        holeNo: row.hole_no,
        reportedBy: row.reported_by,
        announcedAt: row.announced_at ? Date.parse(row.announced_at) : null,
      })
    }

    set({
      cursor: r.version,
      misses: [...mergedMisses.values()],
      doc: r.doc ?? state.doc,
      docVersion,
      serverScores: [...merged.values()],
      confirmations: (r.confirmations ?? []).map((c) => ({
        roundId: c.round_id,
        groupId: c.group_id,
        confirmedBy: c.confirmed_by,
        confirmedAt: c.confirmed_at,
      })),
      // Oldest first, so the feed can reverse it once and the order is stable
      // between polls rather than following whatever the cursor happened to
      // deliver.
      alerts: [...mergedAlerts.values()].sort((a, b) => a.announcedAt - b.announcedAt),
      /*
       * Replaced wholesale, like the confirmations above — there is no merge to
       * do because the server sends all of them every time.
       *
       * An absent field is a server that predates the feature, and standing
       * still is the only honest reading of it; an empty array is a week with no
       * side matches in it, which is a different statement and does clear them.
       */
      sideMatches:
        r.sideMatches === undefined ? state.sideMatches : readSideMatches(r.sideMatches),
      lastSyncAt: Date.now(),
      // A reply is proof of both. See the note in `flush`.
      online: true,
      reachable: true,
      /*
       * Transient errors clear on a good poll; STICKY ones do not, and this
       * line ran every eight seconds — so the standing notices (a dropped
       * score that must be retyped, a deep link that could not be opened)
       * were being erased by the first healthy tick after they appeared,
       * which on a working connection is the same instant. The whole sticky
       * contract, defeated one poll later, by the success path.
       */
      error: sticky ? state.error : null,
    })
  } catch (e) {
    // A reply of any kind proves the server is there; only a request that never
    // got one counts as unreachable. An AbortError from the timeout in `call`
    // has no status, so a hung network now lands here — which is the whole
    // point of giving it a deadline.
    if ((e as { status?: number }).status === undefined) set({ reachable: false })
  } finally {
    pollingSince = 0
  }
}

/**
 * Server rows into the shape the engine reads.
 *
 * `round_ids` is a JSON array in one TEXT column, so it is the one field that can
 * arrive unreadable. A row whose holes cannot be determined is dropped rather
 * than shown: a match with no eighteen behind it would render as all square for
 * money, which is worse than a match that is missing.
 */
function readSideMatches(
  rows: {
    id: string
    round_ids: string
    player_a: string
    player_b: string
    strokes: number
    amount_cents: number
    press_mode: string
    created_by: string
    created_at: string
    voided_at: string | null
    voided_by: string | null
  }[],
): SideMatch[] {
  const out: SideMatch[] = []
  for (const row of rows) {
    let roundIds: string[]
    try {
      const parsed = JSON.parse(row.round_ids) as unknown
      if (!Array.isArray(parsed) || parsed.some((x) => typeof x !== 'string')) continue
      roundIds = parsed as string[]
    } catch {
      continue
    }
    out.push({
      id: row.id,
      roundIds,
      playerA: row.player_a,
      playerB: row.player_b,
      strokes: row.strokes,
      amountCents: row.amount_cents,
      pressMode: row.press_mode as PressMode,
      createdBy: row.created_by,
      createdAt: row.created_at,
      voidedAt: row.voided_at,
      voidedBy: row.voided_by,
    })
  }
  return out
}

export interface TournamentSummary {
  id: string
  name: string
  year: number
  players: { id: string; displayName: string }[]
}

/** Public — the sign-in screen needs a roster before there is a session. */
/**
 * What this phone believes is signed for, including sign-offs still queued.
 *
 * Merged rather than read straight from the server so that tapping Confirm on
 * the 18th green with no signal does what it appears to do — the card reads as
 * confirmed immediately and stays that way while the outbox drains.
 */
let confirmCache: { key: AppState; value: CardConfirmation[] } | null = null

export function mergedConfirmations(s: AppState = state): CardConfirmation[] {
  /*
   * Memoised on state identity, for the same reason `mergedScores` is.
   *
   * This goes straight into `useStore`, so React compares it with Object.is on
   * every render as a `useSyncExternalStore` snapshot. Building a fresh array
   * each call reads as "changed" every time and spins the render loop until
   * React aborts with #185 — a white screen, which has happened here before.
   */
  if (s.confirmOutbox.length === 0) return s.confirmations
  if (confirmCache && confirmCache.key === s) return confirmCache.value
  const value = foldConfirmations(s)
  confirmCache = { key: s, value }
  return value
}

function foldConfirmations(s: AppState): CardConfirmation[] {
  const by = new Map(s.confirmations.map((c) => [`${c.roundId}~${c.groupId}`, c]))
  for (const q of s.confirmOutbox) {
    const key = `${q.roundId}~${q.groupId}`
    if (q.confirm) {
      by.set(key, {
        roundId: q.roundId,
        groupId: q.groupId,
        confirmedBy: s.session?.playerId ?? '',
        // Fixed rather than `Date.now()`: this value is inside a memoised
        // snapshot, and a timestamp that moved on every call would defeat the
        // cache the comment above depends on.
        confirmedAt: s.lastSyncAt ? new Date(s.lastSyncAt).toISOString() : '',
      })
    } else {
      by.delete(key)
    }
  }
  return [...by.values()]
}

/** Sign for a group's card, or (admin) unlock one. Safe with no signal. */
export function setCardConfirmed(roundId: string, groupId: string, confirm: boolean): void {
  set({
    // One entry per card: tapping twice must not queue two writes.
    confirmOutbox: [
      ...state.confirmOutbox.filter((q) => !(q.roundId === roundId && q.groupId === groupId)),
      { roundId, groupId, confirm },
    ],
  })
  void flushConfirmations()
}

// ---------------------------------------------------------------------------
// side matches
// ---------------------------------------------------------------------------

/*
 * Live Nassaus between two men, and the queue that delivers them.
 *
 * These follow the SCORING pattern and not the betting one, which is the
 * opposite of what a bet suggests. The book is online-only on purpose: a wager
 * is an offer at a price and a price goes stale. A side match is not priced by
 * anybody — it is a handshake on the first tee for a fixed amount, and the man
 * making it is standing next to the man taking it. There is nothing to go stale,
 * and the tee is exactly where there is no signal.
 *
 * Nothing here touches the pot, the standings, the payouts or `scheduledPayout`.
 */

let sideCache: { key: AppState; value: SideMatch[] } | null = null

/**
 * What this phone believes is running, including writes still queued.
 *
 * Memoised on state identity, for the third time in this file and for the same
 * reason: this goes straight into `useStore`, React compares the result with
 * Object.is on every render, and a fresh array each call spins the render loop
 * until React aborts with #185.
 */
export function mergedSideMatches(s: AppState = state): SideMatch[] {
  if (s.sideOutbox.length === 0) return s.sideMatches
  if (sideCache && sideCache.key === s) return sideCache.value
  const value = foldSideMatches(s)
  sideCache = { key: s, value }
  return value
}

function foldSideMatches(s: AppState): SideMatch[] {
  const by = new Map(s.sideMatches.map((m) => [m.id, m]))
  for (const q of s.sideOutbox) {
    if (q.kind === 'create') {
      // The server's copy wins if it has one: the other man may already have
      // voided a match this phone is still queuing its own create for, and
      // overwriting that with the optimistic row would revive a dead bet.
      if (!by.has(q.match.id)) by.set(q.match.id, q.match)
      continue
    }
    const cur = by.get(q.matchId)
    if (cur) by.set(q.matchId, { ...cur, voidedAt: q.at, voidedBy: q.by })
  }
  return [...by.values()]
}

export interface SideMatchTerms {
  roundIds: string[]
  playerA: string
  playerB: string
  strokes: number
  amountCents: number
  pressMode: PressMode
  /** Two men who really do want a second bet on the same eighteen. */
  allowDuplicate?: boolean
}

/**
 * Strike a match. Never awaits the network.
 *
 * The id is minted here rather than by the server so the board can be opened on
 * the way out of the setup sheet, with no signal, and still be looking at the
 * same match once the write lands.
 */
export function createSideMatch(terms: SideMatchTerms): SideMatch {
  const match: SideMatch = {
    id: crypto.randomUUID(),
    roundIds: terms.roundIds,
    playerA: terms.playerA,
    playerB: terms.playerB,
    strokes: terms.strokes,
    amountCents: terms.amountCents,
    pressMode: terms.pressMode,
    createdBy: state.session?.playerId ?? '',
    createdAt: new Date().toISOString(),
    voidedAt: null,
    voidedBy: null,
  }
  set({
    sideOutbox: [
      ...state.sideOutbox,
      {
        idempotencyKey: crypto.randomUUID(),
        kind: 'create',
        match,
        ...(terms.allowDuplicate === true ? { allowDuplicate: true } : {}),
      },
    ],
  })
  void flushSideMatches()
  return match
}

/** Call a match off. Either man may. Safe with no signal. */
export function voidSideMatch(matchId: string): void {
  if (state.sideOutbox.some((q) => q.kind === 'void' && q.matchId === matchId)) return
  set({
    sideOutbox: [
      ...state.sideOutbox,
      {
        idempotencyKey: crypto.randomUUID(),
        kind: 'void',
        matchId,
        at: new Date().toISOString(),
        by: state.session?.playerId ?? '',
      },
    ],
  })
  void flushSideMatches()
}

let sideFlushingSince = 0

/**
 * Deliver queued side matches.
 *
 * A 4xx is dropped rather than retried, and said out loud. That is the poison
 * pill lesson from the score outbox, and it bites harder here because there is
 * only ever one entry a man cares about: a create the server will never accept —
 * rounds that do not make eighteen, an amount over the cap — would otherwise sit
 * at the head of this queue for the rest of the week, showing him a match on his
 * Score tab that exists on no other phone.
 */
export async function flushSideMatches(): Promise<void> {
  if (inFlight(sideFlushingSince) || !state.token || !navigator.onLine || state.sideOutbox.length === 0)
    return
  sideFlushingSince = Date.now()
  /*
   * Held, and set at the very end, because the `pull` below clears `error` on
   * success. Setting it inside the loop puts the notice on screen for one frame
   * and then takes it away, which is not a notice — the same lesson the score
   * flush learned about a routine success retracting a standing message.
   */
  let notice: string | null = null
  try {
    for (const q of [...state.sideOutbox]) {
      const done = () =>
        set({
          sideOutbox: state.sideOutbox.filter((x) => x.idempotencyKey !== q.idempotencyKey),
        })
      try {
        if (q.kind === 'create') {
          await call('side-matches', {
            method: 'POST',
            body: JSON.stringify({
              idempotencyKey: q.idempotencyKey,
              id: q.match.id,
              roundIds: q.match.roundIds,
              playerA: q.match.playerA,
              playerB: q.match.playerB,
              strokes: q.match.strokes,
              amountCents: q.match.amountCents,
              pressMode: q.match.pressMode,
              ...(q.allowDuplicate ? { allowDuplicate: true } : {}),
            }),
          })
        } else {
          await call('side-matches', {
            method: 'PATCH',
            body: JSON.stringify({
              idempotencyKey: q.idempotencyKey,
              id: q.matchId,
              action: 'void',
            }),
          })
        }
        done()
      } catch (e) {
        const status = (e as { status?: number }).status
        if (status === undefined || status >= 500) throw e
        notice = `That side match could not be saved: ${
          e instanceof Error ? e.message : 'the server refused it'
        }`
        done()
      }
    }
    await pull()
  } catch {
    // Left in the queue; the next tick tries again. Silent, because a failure
    // that resolves itself is not worth a toast on the 7th tee.
  } finally {
    if (notice !== null) {
      sticky = true
      set({ error: notice })
    }
    sideFlushingSince = 0
  }
}

let confirming = false

/** Deliver queued sign-offs. Each is idempotent, so a replay costs nothing. */
export async function flushConfirmations(): Promise<void> {
  if (confirming || !state.token || !navigator.onLine || state.confirmOutbox.length === 0) return
  confirming = true
  try {
    for (const q of [...state.confirmOutbox]) {
      /*
       * THE SCORES GO FIRST. The sign button is enabled off the MERGED view —
       * server scores folded with this phone's outbox — so on the 18th with no
       * signal a card can be complete locally while its last three holes are
       * still queued. If this small POST then wins the race to the server, the
       * server holds a signed card missing holes, postScores answers every
       * late-arriving write "that card is signed for", flush() drops those
       * keys as poison — and the match settles as final-but-incomplete, which
       * settleHeadToHead pays 0 and $0 TO BOTH SIDES. The winner's money
       * vanishes while every phone says the card was counted.
       *
       * So a sign-off waits its turn: while any score for this round+group
       * sits in the outbox, push the scores and come back on the next tick.
       * Deliberately per-entry rather than a blanket gate, so a signature for
       * a group with nothing queued is never held hostage by someone else's
       * backlog.
       */
      const group = state.doc?.groups.find((g) => g.id === q.groupId)
      const blocking = state.outbox.some(
        (w) => w.roundId === q.roundId && (group?.playerIds.includes(w.playerId) ?? true),
      )
      if (blocking) {
        void flush()
        continue
      }
      await call('confirmations', {
        method: 'POST',
        body: JSON.stringify({
          roundId: q.roundId,
          groupId: q.groupId,
          action: q.confirm ? 'confirm' : 'unlock',
        }),
      })
      /*
       * Drop THIS entry, not every entry for the card.
       *
       * `setCardConfirmed` replaces a card's entry in place, so an admin
       * tapping Unlock while the confirm is still on the wire leaves a
       * different instruction sitting in the queue under the same round and
       * group — and this filter deleted it. The unlock was never sent, nothing
       * was shown, and the next poll put the card back as signed. Every other
       * queue in this file filters by identity; this one matched on the card.
       */
      set({
        confirmOutbox: state.confirmOutbox.filter((x) => x !== q),
      })
    }
    await pull()
  } catch {
    // Left in the queue; the next tick tries again.
  } finally {
    confirming = false
  }
}

export async function fetchTournaments(): Promise<TournamentSummary[]> {
  const r = await call<{ tournaments: TournamentSummary[] }>('tournaments')
  return r.tournaments
}

export async function resetScores(): Promise<void> {
  await call('reset', { method: 'POST', body: JSON.stringify({ confirm: 'RESET' }) })
  // Do not wait for the next poll to make a destructive action look like it
  // worked; drop everything locally now and refetch from a clean server. The
  // side matches were bets on a week that has just stopped existing — the server
  // deleted them, and leaving them on screen here would show money against an
  // empty card until the next poll.
  set({ serverScores: [], outbox: [], cursor: 0, sideMatches: [], sideOutbox: [] })
  await pull()
}

export async function createTournament(
  id: string,
  name: string,
  year: number,
  doc: Tournament,
): Promise<void> {
  await call('tournaments', {
    method: 'POST',
    body: JSON.stringify({ id, name, year, doc }),
  })
}

/**
 * Save the setup, and answer with the version the next edit forks from.
 *
 * `baseVersion` is passed in rather than read from the store because only the
 * caller knows it: it is the version of the document the admin has actually
 * been typing over, which stopped being the store's copy the moment they made
 * their first change. Reading `state.cursor` here — as this did — asked the
 * server a question about scores and used the answer to decide whether a draft
 * was stale.
 */
export async function saveDoc(doc: Tournament, baseVersion: number): Promise<number> {
  const r = await call<{ version: number; docVersion?: number }>('doc', {
    method: 'PUT',
    body: JSON.stringify({ doc, baseVersion }),
  })
  const docVersion = r.docVersion ?? r.version
  /*
   * The sync cursor is deliberately left alone.
   *
   * The write pushed the tournament counter to r.version, but this device has
   * not necessarily received the scores at every version below it — eleven
   * other phones are writing into the same counter. Adopting r.version as the
   * cursor would make the next poll ask for everything ABOVE it and strand
   * those rows here permanently. The document is already current locally; the
   * next poll collects the scores in order, as it would have anyway.
   */
  set({ doc, docVersion, error: null })
  return docVersion
}

/**
 * Add a round nobody planned.
 *
 * Online-only, and unlike a score that is not a compromise: a score is a fact
 * about something that already happened and can wait in the outbox, but a round
 * has to EXIST — with ids the server minted — before anybody can enter a stroke
 * against it, and two phones queuing "an extra nine at Bandon" offline would
 * surface as two rounds when the signal came back.
 *
 * The result is spliced into the local document rather than waited for on the
 * next poll, because the man who added it is standing on the tee and wants to
 * score the first hole now.
 */
export async function addRound(input: {
  date: string
  courseId: string
  nine: 'front' | 'back' | 'full18'
  firstTeeTime: string
  teeIntervalMinutes?: number
  label?: string
  /** Whether it feeds the money and the standings. */
  counts: boolean
  /**
   * A preset id, or 'card'. Deliberately not a config object: the server builds
   * the format itself, so nothing a phone posts can reach `doc_json` and be fed
   * to the scoring engine on twelve devices.
   */
  format: string
  /** Player ids, already seated into groups by the engine. */
  groups: string[][]
  /** The contests inside each group, for the formats that have any. */
  pairings: { groupIndex: number; sides: string[][] }[]
}): Promise<{ roundId: string; matchId: string }> {
  const r = await call<{
    round: Round
    match: Match
    groups: Group[]
    pairings?: Pairing[]
    version: number
    docVersion: number
  }>('rounds', { method: 'POST', body: JSON.stringify(input) })

  /*
   * Checked before it is believed, which is not paranoia at a network boundary:
   * splicing an undefined round into the document puts a hole in the one object
   * every screen reads, and the screen that falls over is whichever one is
   * opened next rather than this one.
   */
  if (!r?.round?.id || !r?.match?.id || !Array.isArray(r.groups)) {
    throw new Error('The round was not created. Try again.')
  }

  /*
   * Spliced only if the poll has not already delivered it.
   *
   * The 8-second poll can land between the server committing this round and its
   * response arriving here, in which case `pull` has already installed a
   * document containing it AND moved the cursor to that version. Appending
   * again would duplicate the round, the match and every group — and it would
   * not heal, because /state only re-ships the document when the cursor is
   * BEHIND doc_version, which it no longer is. The duplicate would then be
   * persisted to localStorage and survive a reload, tabulating every man in the
   * round twice.
   */
  const doc = state.doc
  if (doc && !doc.rounds.some((x) => x.id === r.round.id)) {
    set({
      doc: {
        ...doc,
        rounds: [...doc.rounds, r.round],
        matches: [...doc.matches, r.match],
        groups: [...doc.groups, ...r.groups],
        pairings: [...doc.pairings, ...(r.pairings ?? [])],
      },
      docVersion: r.docVersion,
      error: null,
    })
  }
  // The sync cursor is left alone, for the reason spelled out in `saveDoc`.
  return { roundId: r.round.id, matchId: r.match.id }
}

/**
 * Fetch the week's forecast.
 *
 * Deliberately the most forgiving call in the app: nobody is owed weather, so a
 * failure keeps whatever was last known and says nothing. The server does the
 * talking to the forecaster and caches it for everybody — see `getWeather` —
 * so this is a same-origin call that costs a phone on one bar almost nothing.
 */
export async function fetchWeather(): Promise<void> {
  if (!state.token) return
  try {
    const f = await call<Forecast & { unavailable?: boolean }>('weather')
    if (f && Array.isArray(f.days) && f.days.length > 0) set({ weather: f })
  } catch {
    // Keep the last forecast. A week with no weather on it is still a week.
  }
}

// ---------------------------------------------------------------------------
// the book
// ---------------------------------------------------------------------------

/*
 * Betting is the one part of this app that is deliberately ONLINE-ONLY, and it
 * is worth being explicit about why, because everything above it is built the
 * other way round.
 *
 * Scores must never block on the network: a tap is a fact about something that
 * already happened, so queuing it and reconciling later loses nothing. A bet is
 * the opposite — it is an offer at a price, and the price is only good while the
 * bookie says it is. A wager queued on the 7th and posted forty minutes later
 * when signal returns would be accepted at a line that has since moved, against
 * a market that may already be closed, on an outcome the player might by then
 * have watched happen. That is not sync, it is insider trading with extra steps.
 *
 * So these go straight down the wire and surface their errors, and none of them
 * touch the outbox, the cursor or the merged-scores cache. Nothing here can
 * regress scoring, which is the critical path.
 */

export type MarketStatus = 'proposed' | 'open' | 'closed' | 'settled'
export type MarketResult = 'win' | 'lose' | 'void'
export type BetStatus = 'pending' | 'confirmed' | 'won' | 'lost' | 'void'

/** A market, exactly as the `odds` table stores it — snake_case and all. */
export interface OddsMarket {
  id: string
  market: string
  subject_id: string | null
  description: string
  american_odds: number
  status: MarketStatus
  result: MarketResult | null
  posted_by: string
  posted_at: string
  version: number
}

export interface Bet {
  id: string
  odds_id: string
  player_id: string
  stake_cents: number
  /** The line when the bet was taken. Settlement uses this, not the board. */
  odds_at_placement: number
  status: BetStatus
  confirmed_by: string | null
  confirmed_at: string | null
  payout_cents: number | null
  placed_at: string
  version: number
}

export interface Book {
  odds: OddsMarket[]
  /** The caller's own slips — or everyone's, if the caller is the bookie. */
  bets: Bet[]
  /*
   * Every price each market has shown, oldest first.
   *
   * Read-only history. Nothing settles from it — a placed bet carries its own
   * `odds_at_placement` — it exists to answer "was he always that short?".
   * Optional so an older server that does not send it degrades to no graph
   * rather than to no board.
   */
  history?: OddsPoint[]
}

export interface OddsPoint {
  odds_id: string
  american_odds: number
  posted_at: string
}

/**
 * Fetch the board. Returns rather than storing, like `fetchTournaments`.
 *
 * Kept out of AppState on purpose: AppState is persisted to localStorage and
 * fed to `useSyncExternalStore`, so every field added to it is another thing
 * that can go stale on disk or destabilise a snapshot. The board is cheap,
 * always-fresh-or-nothing, and read by exactly one screen.
 */
/**
 * Take a massage slot, or give one back.
 *
 * The one document write an ordinary player may make — see `postMassage`.
 * Pulls afterwards rather than splicing locally: the server may have
 * refused (somebody beat him to that chair), and the honest answer is
 * whatever the document says, not what this phone hoped.
 */
/**
 * Report a putt missed from inside three feet, or take it back.
 *
 * Straight to the server rather than through the outbox: it is gossip, not
 * scoring, and gossip that fails to send is better lost than replayed onto
 * a hole nobody remembers. The thirty-second delay lives on the server.
 */
export async function markMiss(
  roundId: string,
  playerId: string,
  holeNo: number,
  on: boolean,
): Promise<void> {
  /*
   * Optimistic, because a tap with no answer reads as a broken button.
   *
   * This went out waiting on the round trip: the label only changed once
   * the server had written the row AND a pull had brought it back, which on
   * a fairway is seconds of a man tapping a button that appears dead. The
   * ledger is gossip — nothing settles on it — so showing it immediately
   * and reconciling on the next pull costs nothing and is the only version
   * that feels like a button.
   */
  const before = state.misses
  const key = `${roundId}|${playerId}|${holeNo}`
  set({
    misses: on
      ? [
          ...before.filter((m) => `${m.roundId}|${m.playerId}|${m.holeNo}` !== key),
          { roundId, playerId, holeNo, reportedBy: state.session?.playerId ?? '', announcedAt: null },
        ]
      : before.filter((m) => `${m.roundId}|${m.playerId}|${m.holeNo}` !== key),
  })
  try {
    await call('miss', {
      method: 'POST',
      body: JSON.stringify({ roundId, playerId, holeNo, on }),
    })
  } catch (e) {
    // Put it back the way it was and say so — a silent revert is worse than
    // the wait it replaced.
    set({ misses: before, error: e instanceof Error ? e.message : 'Could not report that' })
    return
  }
  await pull()
}

export async function claimMassage(
  day: string,
  time: string,
  playerId: string | null,
  /** Which of the two tables. Absent means the first chair at that time. */
  table?: number,
): Promise<void> {
  await call('massage', {
    method: 'POST',
    body: JSON.stringify({ day, time, playerId, ...(table != null ? { table } : {}) }),
  })
  await pull()
}

export const fetchBook = (): Promise<Book> => call<Book>('odds')

export async function postOdds(input: {
  market: string
  description: string
  americanOdds: number
  subjectId?: string | null
  /**
   * A player asking rather than the bookie posting. The line lands as
   * 'proposed' — off the board, unbettable — until the bookie accepts it.
   */
  propose?: boolean
}): Promise<{ id: string }> {
  return call<{ id: string }>('odds', {
    method: 'POST',
    body: JSON.stringify({
      market: input.market,
      description: input.description,
      american_odds: input.americanOdds,
      subject_id: input.subjectId ?? null,
      ...(input.propose ? { propose: true } : {}),
    }),
  })
}

/** Move a line, close the book on a market, or settle it. Bookie only. */
export async function updateOdds(
  id: string,
  patch: {
    status?: MarketStatus
    result?: MarketResult | null
    description?: string
    /** The small line under the wording — "Flight 1", "Prop". */
    market?: string
    americanOdds?: number
  },
): Promise<void> {
  await call('odds', {
    method: 'PATCH',
    body: JSON.stringify({
      id,
      ...(patch.status !== undefined ? { status: patch.status } : {}),
      ...(patch.result !== undefined ? { result: patch.result } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
      ...(patch.market !== undefined ? { market: patch.market } : {}),
      ...(patch.americanOdds !== undefined ? { american_odds: patch.americanOdds } : {}),
    }),
  })
}

/**
 * Place a bet.
 *
 * Carries an idempotency key for the same reason a score does: a request whose
 * response was lost must cost nothing to replay. It costs a great deal more here
 * — a duplicated score is visible and fixable, a duplicated $50 is an argument.
 *
 * The key is minted per call rather than per market, so a genuine second tap is
 * a genuine second bet; what it collapses is one request arriving twice.
 */
export async function placeBet(
  oddsId: string,
  stakeCents: number,
  /** The bookie writing a slip for somebody else. Ignored for everyone else. */
  forPlayerId?: string,
): Promise<{ id: string; odds_at_placement: number; status: BetStatus }> {
  return call<{ id: string; odds_at_placement: number; status: BetStatus }>('bets', {
    method: 'POST',
    body: JSON.stringify({
      odds_id: oddsId,
      stake_cents: stakeCents,
      ...(forPlayerId ? { player_id: forPlayerId } : {}),
      idempotencyKey: crypto.randomUUID(),
    }),
  })
}

/** The bookie marking a Venmo as landed, or killing a slip. */
export async function updateBet(
  id: string,
  action: 'confirm' | 'void' | 'archive',
): Promise<void> {
  await call('bets', { method: 'PATCH', body: JSON.stringify({ id, action }) })
}

/**
 * Void, refund and archive every slip — and optionally take the lines down too.
 *
 * A testing tool with real teeth: it puts money back and clears the board in
 * one call. Nothing is deleted, so a mistake is recoverable from the database.
 */
export async function clearBook(markets: boolean): Promise<{ bets: number; markets: number }> {
  return await call('book/clear', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'CLEAR', markets }),
  })
}

/**
 * Wipe one foursome's card, or a whole round, without touching the week.
 *
 * Soft on the server, so the rows keep their history; any signature over those
 * scores is removed with them, since a card cannot vouch for numbers that are
 * gone.
 */
export async function clearScores(
  roundId: string,
  groupId?: string,
): Promise<{ players: number; groups: number; remainingInRound: number }> {
  const r = await call<{ players: number; groups: number; remainingInRound: number }>(
    'scores/clear',
    { method: 'POST', body: JSON.stringify({ roundId, ...(groupId ? { groupId } : {}) }) },
  )
  await pull()
  return r
}

/** Take a market off the board. Refused while it still owes anybody an answer. */
export async function archiveOdds(id: string): Promise<void> {
  await call('odds', { method: 'PATCH', body: JSON.stringify({ id, action: 'archive' }) })
}

/** One cell the photo reader believes it saw on the paper card. */
export interface CardPhotoCell {
  playerId: string
  holeNo: number
  gross: number
}

export interface CardPhotoRead {
  cells: CardPhotoCell[]
  /** Names written on the card that match no player uniquely. Never guessed. */
  unmatched: string[]
  /** Cells the server refused — holes not in the round, grosses off the card. */
  dropped: number
}

/**
 * Have the server read a photograph of the paper card.
 *
 * Online-only and returns rather than storing, like `fetchBook` and for the
 * same reasons: the model is on the other end of the wire, the answer is stale
 * the moment another photo is taken, and a proposal has no business in a
 * persisted AppState. Nothing here writes a score — what comes back is reviewed
 * on screen, and the cells the scorer confirms go through `setScore` one by
 * one, which is the only path that ever changes the card.
 */
export const readCardPhoto = (
  roundId: string,
  groupId: string,
  imageBase64: string,
  mediaType: 'image/jpeg' | 'image/webp',
): Promise<CardPhotoRead> =>
  call<CardPhotoRead>('card-photo', {
    method: 'POST',
    body: JSON.stringify({ roundId, groupId, image: imageBase64, mediaType }),
    /*
     * Its own deadline, not `call`'s eight seconds. That number was measured
     * for a score POST; this request uploads half a megabyte of JPEG over
     * lodge wifi and then waits for a model to look at it, and the person who
     * just photographed a card is standing there watching a spinner — sixty
     * seconds is patience, not a hang.
     */
    signal: AbortSignal.timeout(60_000),
  })

// ---------------------------------------------------------------------------
// lifecycle
// ---------------------------------------------------------------------------

export function startSync(): () => void {
  const tick = () => {
    void flush()
    void flushConfirmations()
    void flushSideMatches()
    void pull()
  }
  // 8s while the app is open and someone is playing; the page being hidden is
  // the real signal that polling can stop, since iOS gives us no background.
  const timer = setInterval(() => {
    if (document.visibilityState === 'visible') tick()
  }, 8000)

  const onOnline = () => {
    // Optimistic on purpose: the next request decides. Staying pessimistic here
    // would leave the offline card up until a write happened to succeed.
    set({ online: true, reachable: true })
    tick()
  }
  const onOffline = () => set({ online: false })
  const onVisible = () => document.visibilityState === 'visible' && tick()

  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  document.addEventListener('visibilitychange', onVisible)
  tick()

  return () => {
    clearInterval(timer)
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
